"use client";

import { Menu } from "@base-ui/react/menu";
import { ChevronDown } from "lucide-react";
import { useLocale } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { FlagIcon } from "@/components/layout/flag-icon";
import { cn } from "@/lib/utils";

const OPTIONS = [
  { locale: "it" as const, country: "it" as const, code: "IT" },
  { locale: "en" as const, country: "gb" as const, code: "EN" },
];

/**
 * Mostra solo la bandiera/lingua ATTIVA. Click apre un mini-menu con l'altra
 * lingua da scegliere — non più le due bandiere affiancate.
 */
export function LanguageSwitch({ className, size = "sm" }: { className?: string; size?: "sm" | "lg" }) {
  const locale = useLocale();
  const pathname = usePathname();
  const large = size === "lg";

  const current = OPTIONS.find((o) => o.locale === locale) ?? OPTIONS[0];
  const others = OPTIONS.filter((o) => o.locale !== locale);

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label="Cambia lingua"
        className={cn(
          "flex items-center font-medium tracking-[0.1em] text-inchiostro hover:text-rosso-pozzo",
          large ? "gap-2 text-base" : "gap-1.5 text-xs",
          className
        )}
      >
        <FlagIcon country={current.country} className={cn("rounded-[1px]", large ? "h-4 w-6" : "h-3 w-4")} />
        {current.code}
        <ChevronDown className={large ? "size-4" : "size-3"} />
      </Menu.Trigger>
      <Menu.Portal>
        {/* z-50: la nav è sticky con z-40 — senza un z-index esplicito qui il
            popup (in un portal, z-index "auto") resta visivamente sotto la nav,
            anche se è dopo nel DOM. Bug reale trovato in verifica manuale
            dell'utente: testo presente e corretto nel DOM, ma coperto. */}
        <Menu.Positioner sideOffset={8} align="end" className="z-50">
          <Menu.Popup className="min-w-[104px] rounded-sm border border-pietra-light/50 bg-bianco-carta p-1 shadow-[0_8px_24px_rgba(28,28,26,0.14)] outline-none">
            {others.map((option) => (
              <Menu.Item
                key={option.locale}
                render={<Link href={pathname} locale={option.locale} />}
                className={cn(
                  "flex items-center gap-2 rounded-[2px] font-medium tracking-[0.08em] text-inchiostro hover:bg-avorio-2 data-[highlighted]:bg-avorio-2",
                  large ? "px-3.5 py-2.5 text-sm" : "px-3 py-2 text-xs"
                )}
              >
                <FlagIcon country={option.country} className={cn("rounded-[1px]", large ? "h-4 w-6" : "h-3 w-4")} />
                {option.code}
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
